import * as _ from 'lodash';
import { ChronoUnit, LocalDate } from '@js-joda/core';

import { CashFlowModel } from 'src/types/cash-flow.model';
import { FullUserModel } from 'src/types/user.model';
import BWDataService from './bw-data.service';
import FormatService from './format.service';
import StorageService from './storage.service';

export default class CashFlowService extends BWDataService {
    public static getCashFlowIndexWithDate(cashFlowResults: CashFlowModel[], date: LocalDate): number {
        // assuming cashFlowResults are ordered by date, returns index in the array containing the given date

        if (!cashFlowResults) return -1;

        let lo: number = 0;
        let hi: number = cashFlowResults.length - 1;
        while (hi >= lo) {
            const mid: number = Math.floor((lo + hi) / 2);
            if (date.isBefore(cashFlowResults[mid].startDate)) {
                hi = mid - 1;
            } else if (!date.isBefore(cashFlowResults[mid].endDate)) {
                lo = mid + 1;
            } else {
                return mid;
            }
        }

        return undefined;
    }

    public static getShortPeriodLabel(date: LocalDate): string {
        switch (StorageService.getZoom()) {
            case 'months':
                return FormatService.formatMonth(date);
            case 'years':
            case 'overall':
                return date.year().toString();
        }
    }

    public static getLongPeriodLabel(date: LocalDate): string {
        switch (StorageService.getZoom()) {
            case 'months':
                return `${FormatService.formatMonth(date)} ${date.year()}`;
            case 'years':
            case 'overall':
                return date.year().toString();
        }
    }

    public static getLastCashFlow(cashFlow: CashFlowModel[]): CashFlowModel {
        return cashFlow?.length && cashFlow[cashFlow.length - 1];
    }

    public static getAgeAtCashFlow(user: FullUserModel, cashFlow: CashFlowModel): number {
        if (!user.dob) return null;
        return Math.max(user.dob.until(cashFlow.startDate.minusDays(1), ChronoUnit.YEARS), 0);
    }

    public static getDateForCashFlow(zoom: string, cashFlow: CashFlowModel): string {
        return FormatService.formatPeriod(zoom, cashFlow.startDate);
    }

    public static isCashFlowInFuture(cashFlow: CashFlowModel): boolean {
        return cashFlow.startDate.isAfter(LocalDate.now());
    }

    public static async getCashflowDetails(type: string, period: string, startDate: LocalDate): Promise<any> {
        const date: string = FormatService.formatYearMonthDate(startDate);
        return await this.get(`/cashFlow/${type}/${period}/${date}`);
    }

    public static getCurrentExpenses(user: FullUserModel): number {
        return this.getExpensesAtDate(user, LocalDate.now());
    }

    public static getCurrentIncome(user: FullUserModel): number {
        return this.getIncomeAtDate(user, LocalDate.now());
    }

    public static getExpensesAtDate(user: FullUserModel, date: LocalDate): number {
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, date);
        let expense: number = 0;
        for (let i = 1; i < 13; i++) {
            const cashFlow: CashFlowModel = user.rawCashFlow[offset - i];
            expense += cashFlow.expense;
        }
        return expense;
    }

    public static getIncomeAtDate(user: FullUserModel, date: LocalDate): number {
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, date);
        let expense: number = 0;
        for (let i = 1; i < 13; i++) {
            const cashFlow: CashFlowModel = user.rawCashFlow[offset - i];
            expense += cashFlow.income;
        }
        return expense;
    }

    public static getPastIncome(user: FullUserModel): number {
        const offset = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const pastCashflow = user.rawCashFlow.slice(1, offset);
        return _.sumBy(pastCashflow, 'income');
    }

    public static getPastExpenses(user: FullUserModel): number {
        const offset = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const pastCashflow = user.rawCashFlow.slice(1, offset);
        return _.sumBy(pastCashflow, 'expense');
    }

    public static getCashFlowTotalForPeriod(user: FullUserModel, dataType: string, monthsInPast: number = 12): number {
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const yearOfCashFlows: CashFlowModel[] = user.rawCashFlow.slice(offset - 12, offset);
        return _.sumBy(yearOfCashFlows, dataType) || 0;
    }
}
